import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ChartBar, Receipt, UsersThree, Package, Tag, IdentificationCard,
  Gear, SignOut, List, X, Plus, FolderOpen, Percent,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const adminNav = [
  { to: "/dashboard", label: "Dashboard", icon: ChartBar },
  { to: "/pedidos", label: "Pedidos", icon: Receipt },
  { to: "/clientes", label: "Clientes", icon: UsersThree },
  { to: "/produtos", label: "Produtos", icon: Package },
  { to: "/tabelas", label: "Tabelas de Preço", icon: Tag },
  { to: "/comissoes", label: "Comissões", icon: Percent },
  { to: "/materiais", label: "Materiais", icon: FolderOpen },
  { to: "/representantes", label: "Representantes", icon: IdentificationCard },
  { to: "/configuracoes", label: "Configurações", icon: Gear },
];
const repNav = [
  { to: "/pedidos", label: "Pedidos", icon: Receipt },
  { to: "/clientes", label: "Clientes", icon: UsersThree },
  { to: "/materiais", label: "Materiais", icon: FolderOpen },
];

function Logo() {
  return <img src="/dualt-logo.png" alt="DUALT" className="h-8 w-auto object-contain" />;
}

export default function AppLayout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = isAdmin ? adminNav : repNav;

  const NavItems = () => (
    <nav className="flex flex-col gap-1 px-3">
      {nav.map((n) => {
        const Icon = n.icon;
        return (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`nav-${n.to.replace("/", "")}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-accent text-white" : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
              }`
            }
          >
            <Icon size={20} weight="bold" />
            {n.label}
          </NavLink>
        );
      })}
    </nav>
  );

  const Sidebar = () => (
    <div className="flex h-full flex-col bg-zinc-950">
      <div className="flex items-center justify-between px-5 h-16 border-b border-zinc-800">
        <Logo />
        <button className="lg:hidden text-zinc-400" onClick={() => setOpen(false)} data-testid="close-sidebar">
          <X size={22} />
        </button>
      </div>
      <div className="flex-1 py-5 overflow-y-auto">
        <NavItems />
      </div>
      <div className="border-t border-zinc-800 p-4">
        <div className="mb-3">
          <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
          <p className="text-xs text-zinc-500 capitalize">{user?.role}</p>
        </div>
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          data-testid="logout-btn"
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-zinc-400 hover:bg-zinc-800 hover:text-white transition-colors"
        >
          <SignOut size={18} weight="bold" /> Sair
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* desktop sidebar */}
      <aside className="hidden lg:block w-64 fixed inset-y-0 left-0 z-30">
        <Sidebar />
      </aside>

      {/* mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64"><Sidebar /></div>
          <div className="flex-1 bg-black/50" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 flex items-center justify-between h-16 px-4 sm:px-6 bg-white/90 backdrop-blur border-b border-border">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="open-sidebar">
              <List size={24} />
            </button>
            <div className="lg:hidden"><Logo /></div>
          </div>
          <Button
            data-testid="new-order-header-btn"
            onClick={() => navigate("/pedidos/novo")}
            className="bg-accent hover:bg-accent/90 text-white gap-2"
          >
            <Plus size={18} weight="bold" /> Novo Pedido
          </Button>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 fade-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
